import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Droplet } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { Reveal, MaskLine } from "../components/Reveal";
import emblem from "../assets/banner.png";

const STORY_HERO =
  "https://images.unsplash.com/photo-1484387436194-cf7cb70800ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const IMG_BASS =
  "https://images.pexels.com/photos/13740880/pexels-photo-13740880.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800";
const IMG_LAKE =
  "https://images.unsplash.com/photo-1671485927315-f56c7a65a051?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const IMG_DUSK =
  "https://images.unsplash.com/photo-1596796571148-77b6e70dd6b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const LEAD =
  "Drift began with a lifetime of angling and a simple conviction: there are still great fishing products waiting to be created.";
const INTRO_BODY = [
  "For years, we spent countless hours on the water — learning, observing, and asking questions. We celebrated the products that helped shape the sport we love, but we also recognized there were still opportunities to improve.",
  "Too often, new products looked like old ideas with a different color or a new package. We believed anglers deserved more than that.",
];

const SECTIONS = [
  {
    n: "01",
    title: "Why We Exist",
    img: IMG_BASS,
    body: [
      "We didn’t start Drift to become another name in the fishing industry. We started Drift to challenge conventional thinking. Every product begins with a question: “What problem does an angler still have that deserves a better solution?”",
      "We’re not interested in creating another version of something simply because it’s popular. We find the places where anglers still need better — and go to work.",
    ],
    quote: "Purpose comes before product.",
  },
  {
    n: "02",
    title: "Our Philosophy",
    body: [
      "The best products aren’t created by chasing trends. They’re created by understanding nature. We study how baitfish move. We watch how predators react. We obsess over the smallest details, because in fishing, subtle differences create the biggest results.",
      "Finesse isn’t just a technique to us — it’s a philosophy. The goal isn’t more action. It’s the right action. We don’t force movement into a bait; we remove everything that stops it from moving naturally. Every curve. Every taper. Every tail.",
    ],
  },
  {
    n: "03",
    title: "What Drift Means",
    img: IMG_LAKE,
    body: [
      "Our name is the philosophy. Drift is the natural movement that happens when something is allowed to behave exactly as it was designed.",
      "Natural movement. Purposeful design. Effortless action. Nature has already solved many of the problems we’re trying to understand. Our job is to pay attention.",
    ],
    quote: "Nature already solved it. We just listen.",
  },
  {
    n: "04",
    title: "Products With a Purpose",
    body: [
      "Whether it’s our Phantom™ swimbait, Covert™ lizard, Pulse™ worm, or one still waiting to be poured — every Drift bait must earn its place. Before it carries our name, we ask: What problem does it solve? Would we confidently fish it ourselves? Does it meet The Drift Standard?",
      "We’re not here to release products because the market expects something new. We build baits that deserve to exist — that perform, that inspire confidence, that help you catch a fish, catch a moment, catch a memory.",
    ],
  },
  {
    n: "05",
    title: "More Than a Sale",
    img: IMG_DUSK,
    body: [
      "Our success will never be measured by how many packages we sell. The greatest compliment isn’t that someone bought a Drift bait — it’s that they tied it on again.",
      "The first cast comes from curiosity. The next comes from confidence. Eventually, confidence becomes trust. We don’t believe we’re entitled to your loyalty. We intend to earn it.",
    ],
    quote: "The first cast is curiosity. The next is trust.",
  },
  {
    n: "06",
    title: "Our Promise",
    body: [
      "As long as the Drift name appears on a package, we’ll keep designing, testing, refining, and pouring products faithful to our purpose. Every new bait carries the same curiosity, the same passion, the same pursuit of better that inspired the very first Drift bait ever poured.",
      "We may never become the biggest name in fishing. But every day, we’ll work to become one of the most trusted.",
    ],
  },
];

const StorySection = ({ s, i }) => {
  const hasImg = Boolean(s.img);
  const imageRight = i % 2 === 1;

  if (hasImg) {
    return (
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        <Reveal className={imageRight ? "lg:order-2" : ""}>
          <motion.div
            initial={{ scale: 1.06 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
            className="relative overflow-hidden border border-white/5 aspect-[4/5]"
          >
            <img src={s.img} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#04070A]/70 via-transparent to-transparent" />
            <span className="absolute bottom-4 left-5 font-mono-plex text-7xl md:text-8xl font-medium text-white/15 leading-none select-none">
              {s.n}
            </span>
          </motion.div>
        </Reveal>
        <Reveal delay={0.12} className={imageRight ? "lg:order-1" : ""}>
          <div className="h-px w-16 bg-orange-500 mb-6" />
          <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-[0.9]">{s.title}</h2>
          <div className="mt-6 space-y-5">
            {s.body.map((p, j) => (
              <p key={j} className="text-slate-300 text-base md:text-lg leading-relaxed">{p}</p>
            ))}
          </div>
          {s.quote && (
            <p className="font-display text-2xl md:text-3xl text-orange-500 tracking-tight mt-8 leading-tight">
              {s.quote}
            </p>
          )}
        </Reveal>
      </div>
    );
  }

  return (
    <Reveal>
      <div className="relative grid md:grid-cols-12 gap-6 md:gap-10 border-l-2 border-orange-500/40 pl-6 md:pl-10">
        <span className="pointer-events-none absolute -top-10 right-0 font-mono-plex text-[8rem] md:text-[12rem] font-medium text-white/[0.04] leading-none select-none">
          {s.n}
        </span>
        <div className="md:col-span-4">
          <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">Chapter {s.n}</span>
          <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter mt-3 leading-[0.9]">{s.title}</h2>
        </div>
        <div className="md:col-span-8 relative">
          <div className="space-y-5">
            {s.body.map((p, j) => (
              <p key={j} className="text-slate-300 text-base md:text-lg leading-relaxed">{p}</p>
            ))}
          </div>
          {s.quote && (
            <p className="font-display text-2xl md:text-3xl text-orange-500 tracking-tight mt-8 leading-tight">{s.quote}</p>
          )}
        </div>
      </div>
    </Reveal>
  );
};

export default function Story() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#04070A] min-h-screen" data-testid="our-story-page">
      <Nav />

      {/* HERO */}
      <section className="relative min-h-[78vh] flex flex-col justify-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={STORY_HERO} alt="Water at dusk" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#04070A] via-[#04070A]/70 to-[#04070A]/30" />
        </div>
        <div className="relative z-10 max-w-[1600px] w-full mx-auto px-6 md:px-10 pb-20 pt-40">
          <MaskLine delay={0.15}>
            <span className="font-mono-plex text-xs tracking-[0.3em] uppercase text-orange-500">Drift Tackle Co. — Our Story</span>
          </MaskLine>
          <h1 className="font-display font-semibold uppercase tracking-tighter leading-[0.82] text-6xl sm:text-8xl lg:text-[8.5vw] mt-6">
            <MaskLine delay={0.3}>Born From a</MaskLine>
            <MaskLine delay={0.42}>Lifetime on</MaskLine>
            <MaskLine delay={0.54}>the Water</MaskLine>
          </h1>
        </div>
      </section>

      {/* OPENING STATEMENT */}
      <section className="border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">The Beginning</span>
            <p className="font-display text-3xl sm:text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95] mt-6">
              {LEAD}
            </p>
          </Reveal>
          <div className="lg:col-span-5 lg:pt-14 space-y-6">
            {INTRO_BODY.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.1}>
                <p className="text-slate-400 text-base md:text-lg leading-relaxed">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ORANGE PULL-QUOTE BAND */}
      <section className="relative bg-orange-500 text-[#04070A] overflow-hidden">
        <Droplet className="absolute -right-10 -bottom-16 w-80 h-80 text-[#04070A]/10 rotate-12 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <span className="font-mono-plex text-xs tracking-[0.3em] uppercase text-[#04070A]/70">The Conviction</span>
            <p className="font-display text-4xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.9] mt-5 max-w-4xl">
              There are still great baits left to be made.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CHAPTERS */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col gap-24 md:gap-36">
          {SECTIONS.map((s, i) => (
            <StorySection key={s.n} s={s} i={i} />
          ))}
        </div>
      </section>

      {/* CLOSING */}
      <section className="bg-[#0A1118] border-t border-white/5 py-28 md:py-36">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center flex flex-col items-center">
          <Reveal>
            <img src={emblem} alt="Drift Tackle Co. emblem" className="h-28 w-28 object-contain mix-blend-screen mx-auto mb-10" />
            <h2 className="font-display text-5xl sm:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.85]">
              Welcome to Drift.<br /><span className="text-orange-500">Earn the next cast.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/shop"
              className="group inline-flex items-center gap-3 bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-orange-600 active:scale-95 transition-all mt-12"
              data-testid="story-shop-button"
            >
              Explore the Range
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
